import React, { useState, useEffect, useRef } from 'react';
import Table from './Table';
import InfoPage from './InfoPage';
import marieStoryData from './storyData.json';
import Quiz from './Quiz';

// Hauptkomponente der Anwendung, die die Geschichte und Navigation verwaltet.
const App = () => {
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0); // Zustand für den aktuellen Kapitelindex.
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Zustand für die Sichtbarkeit des Hamburger-Menüs.
  const [isInfoOpen, setIsInfoOpen] = useState(false); // Zustand für die Sichtbarkeit der Info-Seite.
  const mainContentRef = useRef(null);

  // Scrollt beim Kapitelwechsel automatisch zum Seitenanfang und schließt das Menü.
  useEffect(() => {
    // Scrolle den <main>-Container nach oben
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
    // Scrolle zusätzlich das Hauptfenster, was auf Mobilgeräten hilft
    window.scrollTo(0, 0);
    setIsMenuOpen(false);
  }, [currentChapterIndex]); // Dieser Effekt wird bei jeder Änderung von currentChapterIndex ausgelöst.

  // Definition der gesamten Geschichte "Marie und die Geldanlage" mit Kapiteln und deren Inhalten.
  const marieStory = marieStoryData;

  const currentChapter = marieStory.chapters[currentChapterIndex];
  const isFirstChapter = currentChapterIndex === 0;
  const isLastChapter = marieStory.chapters.length - 1 === currentChapterIndex;

  // Handler für das vorherige Kapitel.
  const handlePreviousChapter = () => {
    if (!isFirstChapter) {
      setCurrentChapterIndex(currentChapterIndex - 1);
    }
  };

  // Handler für das nächste Kapitel.
  const handleNextChapter = () => {
    if (!isLastChapter) {
      setCurrentChapterIndex(currentChapterIndex + 1);
    }
  };

  // Handler für die Auswahl eines Kapitels aus dem Menü.
  const handleChapterSelect = (index) => {
    setCurrentChapterIndex(index);
    setIsMenuOpen(false); // Menü schließen nach Auswahl.
  };

  return (
    // Äußerer Container, der das "Telefon"-Layout auf großen Bildschirmen erstellt.
    <div className="flex justify-center items-center bg-gray-100 md:bg-white md:p-8">
      {/* Hintergrund für info popup */}
      {isInfoOpen && (
        <div
          className="absolute inset-0 flex justify-center items-center z-50 "
        >
        </div>
      )}

      {/* Simulierter Notch/Kamera-Bereich für das Telefon-Layout */}
      <div className="relative w-full h-full md:w-[414px] md:h-[896px] md:rounded-[3rem] md:shadow-2xl md:border-[10px] md:border-gray-800 md:overflow-hidden bg-white flex flex-col">
        {isInfoOpen && (
          <div className="absolute inset-0 flex justify-center items-center z-50" onClick={() => setIsInfoOpen(false)}>
            <InfoPage onClose={() => setIsInfoOpen(false)} />
          </div>
        )}

        <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap" rel="stylesheet" />
        <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-xl z-50"></div>
        <header className="relative pt-8 pb-4 px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`fixed md:absolute top-4 left-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#008080] z-50 ${isMenuOpen || isInfoOpen ? 'hidden' : ''}`}
            aria-label="Menü öffnen"
          >
            <svg className="h-8 w-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <button
            onClick={() => setIsInfoOpen(!isInfoOpen)}
            className={`fixed md:absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#008080] z-50 ${isMenuOpen || isInfoOpen ? 'hidden' : ''}`}
            aria-label="Info öffnen"
          >
            <svg className="h-8 w-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </header>

        {/* Sidebar-Menü für die Kapitelauswahl. */}
        <div className={`fixed inset-y-0 left-0 w-full bg-white shadow-lg transform ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out z-40 flex flex-col md:absolute md:rounded-[3rem]`}>
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800 font-open-sans">Inhalt</h3>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#008080]"
              aria-label="Menü schließen"
            >
              <svg className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">
              {marieStory.chapters.map((chapter, index) => {
                if (chapter.hideFromContents) {
                  return null;
                }
                return (
                  <li key={index}>
                    <button
                      onClick={() => handleChapterSelect(index)}
                      className={`block w-full text-left p-2 font-open-sans text-lg transition-colors ${index === currentChapterIndex ? 'text-[#008080] font-bold bg-green-50' : 'text-gray-700 hover:text-[#008080] hover:bg-gray-50'}`}
                    >
                      {chapter.title}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        {isMenuOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-30 md:absolute md:rounded-[3rem]" onClick={() => setIsMenuOpen(false)}></div>
        )}

        <main ref={mainContentRef} className="flex-grow overflow-y-auto px-10 sm:px-6 lg:px-10 pb-20 flex flex-col">
          <h2 className="text-2xl sm:text-3xl font-normal text-gray-800 text-left pt-8 pb-3">
            {currentChapter.title}
          </h2>
          <div className="flex-grow space-y-4 text-base text-gray-700 leading-relaxed font-open-sans font-normal">
            {currentChapter.content.map((block, index) => {
              const key = `${currentChapterIndex}-${index}`;
              if (block.type === 'paragraph') {
                return <p key={index} dangerouslySetInnerHTML={{ __html: block.text }}></p>;
              } else if (block.type === 'image') {
                const sizeClass = block.size || 'max-w-full h-auto max-h-80 object-contain';
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className={sizeClass}
                      onError={(e) => { e.target.onerror = null; e.target.src = `/images/placeholder.png`; }}
                    />
                  </div>
                );
              } else if (block.type === 'image-large') {
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className="max-w-full h-auto max-h-full object-contain"
                      onError={(e) => { e.target.onerror = null; e.target.src = `/images/placeholder.png`; }}
                    />
                  </div>
                );
              } else if (block.type === 'animation') {
                const sizeClass = block.size || 'max-w-full h-auto max-h-80 object-contain';
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className={sizeClass}
                    />
                  </div>
                );
              } else if (block.type === 'animation-large') {
                return (
                  <div key={`${currentChapterIndex}-${index}`} className="flex justify-center my-6 px-4">
                    <img
                      src={block.src}
                      alt={block.alt}
                      className="max-w-full h-auto max-h-full object-contain"
                      onError={(e) => { e.target.onerror = null; e.target.src = `/images/placeholder.png`; }}
                    />
                  </div>
                );
              } else if (block.type === 'link') {
                return (
                  <span key={key}>
                    <a href={block.href} target="_blank" rel="noopener noreferrer" className="text-[#008080] hover:underline font-semibold">
                      {block.text}
                    </a>
                    {' '}
                  </span>
                );
              } else if (block.type === 'customTable') {
                return <Table key={index} data={block.data} />;
              } else if (block.type === 'quiz') {
                return <Quiz key={index} quizData={block} />;
              }
              return null;
            })}
          </div>
          <footer className="text-center pt-8">
            <button
              onClick={() => handleChapterSelect(marieStory.chapters.length - 1)}
              className="text-[#008080] hover:text-[#006666] hover:underline font-open-sans"
            >
              Impressum
            </button>
          </footer>
        </main>

        <div className="fixed md:absolute bottom-0 left-0 right-0 p-4 flex justify-between items-center z-20 pointer-events-none">
          <button
            onClick={handlePreviousChapter}
            disabled={isFirstChapter}
            className={`p-3 rounded-full transition-colors bg-[#008080] text-white pointer-events-auto ${isFirstChapter ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'hover:bg-[#006666] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50'}`}
            aria-label="Vorheriges Kapitel"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={handleNextChapter}
            disabled={isLastChapter}
            className={`p-3 rounded-full transition-colors bg-[#008080] text-white pointer-events-auto ${isLastChapter ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'hover:bg-[#006666] focus:outline-none focus:ring-2 focus:ring-[#008080] focus:ring-opacity-50'}`}
            aria-label="Nächstes Kapitel"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;
